/**
 * One instanced pool of searchlight beams — the drawable half of
 * `flight/searchlights.ts`, and the whole GPU footprint of the night lights.
 *
 * **One instanced mesh, one draw call, no texture** — the beams alone; the
 * three articulated pieces of the projector under them are
 * `searchlight-mount.ts`'s. A beam is a long thin open cone with
 * its apex at the lamp's aperture, carried by the instance matrix: the shared
 * geometry is a unit cone (apex at the origin, axis `+Y`, base one unit up),
 * and the per-instance matrix rotates `+Y` onto the beam's direction and scales
 * the whole thing by `BEAM_LENGTH_M`. Scaling uniformly is what keeps the cone
 * *angle* constant — a beam that got longer without getting wider would be a
 * needle, and one that got wider without getting longer would be a floodlight.
 *
 * ## The material: `transparent: true`, and what the swapchain actually did
 *
 * A beam is the one effect in this port that genuinely **adds** rather than
 * covers, so `AdditiveBlending` is the whole point of it. This file used to
 * carry that mode on an **opaque** material, and cited
 * `WebGPUPipelineUtils.js` line 104 for why that is legal here —
 * `material.blending !== NoBlending && (material.blending !== NormalBlending || material.transparent !== false)`
 * — a condition which is still in the installed three (0.185.1) and still reads
 * that way. **The blend state is programmed and the pass still did not
 * composite.** Read off the real swapchain, `?m=balloon-busting&time=night`,
 * fly-by camera, one frame in which a single beam crosses both the night sky
 * and the dark ground:
 *
 * | destination | destination RGB | beam fragment, `transparent: false` | with `transparent: true` |
 * | --- | --- | --- | --- |
 * | night sky | `(32, 57, 77)` | `(167, 190, 189)` | `(199, 247, 255)` |
 * | dark ground | `(28, 44, 16)` | `(176, 193, 188)` | `(204, 237, 204)` |
 *
 * Opaque, the fragment **replaced** its destination: two destinations 61 apart
 * in blue produced results **1 apart**, and each value is exactly this file's
 * own `mix(BEAM_NEAR_COLOUR, BEAM_FAR_COLOUR, along)` with no contribution from
 * what was already there. Transparent, every channel is the sum —
 * `32 + 167 = 199`, `57 + 190 = 247`, `77 + 189 = 266` clamped to `255`,
 * `28 + 176 = 204`, `44 + 193 = 237`, `16 + 188 = 204` — six of six, with the
 * single clamp where saturation is arithmetically due. The same pair measured
 * against `?treatment=measured`'s flat `BARON.PAL` 48 sky `(125, 182, 195)`
 * gives the starkest form of it: opaque, that destination and the night sky are
 * **93 apart in red and both return `(163, 189, 189)`**.
 *
 * **Rule (`lessons.md`, 2026-08-23, tracer lane): a source citation is not a
 * measurement.** The condition being true in the backend's source says the
 * blend state is *programmed*; only a swapchain capture says what the pass did.
 *
 * The reversed-Z trap this collides with was A/B'd rather than assumed. This
 * repo records four occasions on which `transparent: true` or
 * `depthWrite: false` made a pooled `InstancedMesh` rasterize zero pixels
 * (`lessons.md` 2026-08-19, 2026-08-22 twice, 2026-08-23). Here the beams
 * **drew** with the flag on, at the peaks tabulated above — consistent with the
 * 2026-08-22 entry's own scope, since a pool that vanishes is one that was in
 * the scene graph before it had a transform, and this one is `visible = false`
 * until it has instances. `depthWrite` stays `true` regardless, which is the
 * conservative half: see below.
 *
 * ## Depth, and why `depthWrite` did not move with `transparent`
 *
 * `depthWrite: true` is kept, so the occlusion behaviour is **byte-for-byte
 * what it was** and only the blending changed. Opaque, the beams drew in the
 * opaque list and every transparent thing in the scene already came after them;
 * transparent, they draw at `SEARCHLIGHT_RENDER_ORDER` inside the transparent
 * list, which `three/transparent-sort.ts` orders
 * `prop disc (1) -> searchlights (3) -> tracers (4) -> dash quad (5)`. Either
 * way a tracer behind a beam's near wall is rejected and one in front of it is
 * not, so nothing that was visible before has become hidden.
 *
 * Two crossing beams still both add, which is worth stating because it is the
 * case a depth-writing transparent surface could plausibly have broken: the
 * transparent list is drawn far-to-near, so the far beam writes depth and the
 * near one is *in front* of that write and passes.
 *
 * The move into the transparent list also fixes a latent ordering fault this
 * file's old doc asserted its way past. "The beams draw after every other
 * opaque thing" was not true: the **opaque** list is reversed by reversed-Z as
 * well (`transparent-sort.ts`'s closing note), so a `renderOrder` of 3 drew the
 * beams *early* among opaque geometry and any opaque object behind them in the
 * ladder painted over them. In the transparent list they genuinely do come
 * after all opaque geometry, which is what makes a caught aeroplane light up.
 *
 * `FrontSide`, deliberately. A closed cone shows the camera a near face and a
 * far face, and with additive blending both would contribute — but which of
 * them survives the depth test depends on the order the two triangles happen to
 * sit in the index buffer, which is exactly the "triangle order inside a shared
 * `BufferGeometry`" tie the z-fighting lane names as the one no scene-graph
 * handle can reach. Drawing only the near face removes the tie instead of
 * biasing it. The cost is that a camera *inside* a beam sees nothing, which is
 * a fair trade for a case that lasts a frame or two per sortie.
 */

import * as THREE from "three/webgpu";
import { ditherThreshold } from "./ordered-dither.ts";

/**
 * How far a beam throws, in metres. `AA.DAT`'s altitude ceiling is 1 097 m
 * (`combat/flak-shell.ts`), so a beam has to reach well past the highest
 * aeroplane its own guns will ever engage or it would stop short of the thing
 * it is looking for. Four kilometres also matches what the lights of the period
 * actually managed — a 90 cm carbon arc was quoted at three to five.
 */
export const BEAM_LENGTH_M = 4000;

/**
 * The beam's half-angle. Real searchlight beams are between one and two degrees
 * across; 1.2 puts the far end 84 m wide at `BEAM_LENGTH_M`, which is a shaft
 * rather than a wedge and still thick enough to catch an aeroplane in.
 */
export const BEAM_HALF_ANGLE_DEG = 1.2;

/**
 * The beam at the emplacement: `#ccccb9`, a **derived** colour, and the one
 * synthetic ink in this file. Its two parents are both real `BARON.PAL`
 * entries, and it is the pair rather than either of them because the two halves
 * of a colour were wanted from different places.
 *
 * *Where the hue comes from.* `BARON.PAL` **209 `#ffffe7`** is the head of the
 * palette's own incandescence ramp — 209-215 walks white-hot → `#fbfb00`
 * yellow → `#f7c300` → `#f38e00` → `#eb2800` red, which is what this game
 * paints a thing that is burning. A searchlight of the period *was* a burning
 * thing: two carbon rods struck into an arc at something under 4 000 K, which
 * is precisely why "the beam is warm" is a fact about the hardware and not a
 * mood. 209's chromaticity is white with the blue pulled down ten per cent —
 * a hair of cream, not amber — and that is the whole of the warmth here.
 *
 * *Where the brightness comes from.* `BARON.PAL` **199 `#cbcbcb`**, the entry
 * the beam used before this and the one the original's own **moon** is drawn in
 * (`SUN.BMP` frame 1, `flight/time-of-day.ts`). The user asked for a warmer
 * cone, not a brighter one, and 209 is 25 % brighter than 199 — taking it whole
 * would have blown out the near end of a beam that already needed
 * `BEAM_MAX_COVERAGE` to stop it rasterizing as a wall.
 *
 * So: 209's chromaticity, renormalised to 199's Rec. 709 luminance.
 * `203 / luma(255,255,231) = 0.80153`, giving `(204, 204, 185)` at luma 202.6
 * against 199's 203.0 — the same light, four tenths of a unit dimmer and a
 * carbon arc instead of a moon. The precedent for deriving rather than picking
 * is the map's synthetic inks (`map-paint.ts`); the discipline is the same one,
 * that a made-up colour states both of its parents and the arithmetic between
 * them.
 *
 * `BEAM_FAR_COLOUR` is deliberately left alone, which makes the ramp *more*
 * coherent rather than less: the warm end is the arc, and the cold end is where
 * the shaft has stopped being lamplight and become scattered air — which really
 * is blue, and which the palette really does keep a ladder of (195-198, the
 * stars).
 */
export const BEAM_NEAR_COLOUR = 0xccccb9;

/**
 * `BARON.PAL` 195 `#71aac3` — the beam at its far end. 195-198 is the ramp the
 * original reserves for **stars** (`STARS.DAT`'s four magnitudes,
 * `docs/engineering/time-of-day-design.md` §5), and it runs cold blue up to the
 * near-white of 199. So the palette already contains a light-in-the-night-sky
 * ladder and the beam simply walks it: near-white where the arc is, cold blue
 * where the shaft dissolves into the dark. Nothing was invented for this.
 */
export const BEAM_FAR_COLOUR = 0x71aac3;

/**
 * The beams' rung of the transparent ladder — above the propeller disc's `1`
 * and below `TRACER_RENDER_ORDER`'s `4`, giving
 * `prop disc -> searchlights -> tracers -> dash quad` once
 * `three/transparent-sort.ts` has undone the renderer's reversal.
 *
 * The number is unchanged from when this material was opaque, but its *meaning*
 * changed with `transparent: true`: it is now read out of three's transparent
 * render list rather than its opaque one, which is the list this port's ladder
 * was always written for. Being above the disc is no longer "tidiness only" —
 * the two are now sorted against each other for real.
 */
export const SEARCHLIGHT_RENDER_ORDER = 3;

/** The coverage cut. The same half every other pool in this renderer uses, so nothing moirés against anything. */
const BEAM_ALPHA_TEST = 0.5;

/**
 * Where the stipple starts thinning, as a fraction of the beam's length.
 * Densest over the first sixth and dissolving from there to nothing at the
 * tip, which is how a beam in haze actually goes — brightest at the mouth,
 * lost in the dark long before its geometry ends.
 */
const FALLOFF_START = 0.15;

/**
 * The ceiling on a beam's stipple density, and the reason it is a *scale* and
 * not a clamp.
 *
 * Measured on the real GPU: without it, a beam near the camera rasterized as a
 * hard-edged **solid white wedge** across half the screen. Nothing was wrong
 * with the geometry — the coverage simply reached 1 near the arc, so every
 * fragment cleared the dither ladder and the stipple stopped being a stipple.
 * A light you cannot see through is a wall, and this one was painting out the
 * aeroplane it had just caught.
 *
 * Scaling rather than clamping keeps the brightness ladder the aiming law
 * builds — idle lands near 0.34, an acquiring gun near 0.48, a gun that has
 * ranged in at the full 0.62 — so a beam still visibly hardens when it finds
 * you, and none of the three is ever opaque.
 */
const BEAM_MAX_COVERAGE = 0.62;

/**
 * Beam geometry closer to the lens than this fades out entirely, over
 * `NEAR_FADE_FULL_M`.
 *
 * The same call `effect-sprites.ts` makes with `LENS_CLEARANCE_M` and
 * `scene-particles.ts` repeats for a particle's radius: an effect the camera is
 * *inside* is not an effect, it is a full-screen fill. A beam is the extreme
 * case because it is four kilometres long — the fly-by camera can and does end
 * up within a few tens of metres of one, where a cone 84 m across at its mouth
 * subtends most of the view.
 *
 * Measured as a euclidean range rather than a view-space depth, which is the
 * 2026-08-22 sprite-sizing lesson's own rule: a range cull that uses depth
 * changes its answer when the pilot turns his head, and a beam must not pop
 * because you glanced at it.
 */
const NEAR_FADE_START_M = 120;
/** Range at which a beam is back to its full density. */
const NEAR_FADE_FULL_M = 420;

const scratchMatrix = new THREE.Matrix4();
const scratchPosition = new THREE.Vector3();
const scratchDirection = new THREE.Vector3();
const scratchQuaternion = new THREE.Quaternion();
const scratchScale = new THREE.Vector3();
/** The axis the shared cone is built along, which `setFromUnitVectors` rotates onto each beam. */
const CONE_AXIS = new THREE.Vector3(0, 1, 0);

/**
 * A unit cone with its **apex at the origin** and its base one unit along `+Y`,
 * open-ended so there is no disc capping the far end for the camera to catch
 * side-on.
 *
 * `ConeGeometry` builds apex-up about the centre (apex `+0.5`, base `-0.5`);
 * turning it over and lifting it by a half puts the apex on the origin, which
 * is what lets the instance matrix treat the emplacement as the beam's
 * position and the scale as its length. The flip is a rotation, so the winding
 * and the normals come with it and `FrontSide` still means the near face.
 */
function beamGeometry(): THREE.ConeGeometry {
  const radius = Math.tan((BEAM_HALF_ANGLE_DEG * Math.PI) / 180);
  const geometry = new THREE.ConeGeometry(radius, 1, 12, 1, true);
  geometry.rotateX(Math.PI);
  geometry.translate(0, 0.5, 0);
  return geometry;
}

/**
 * Exported so `apps/web/test/searchlight-material.test.ts` can pin the depth
 * and blending flags without a GPU — the same arrangement
 * `bulletTracerMaterial` and `sheetMaterial` have, and for the same reason:
 * these four flags are the ones this renderer has already broken twice.
 */
export function searchlightBeamMaterial(): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.FrontSide });
  material.blending = THREE.AdditiveBlending;
  // `true`, and it is the one flag that makes this material a light rather than
  // a paint — see the module doc's "what the swapchain actually did".
  material.transparent = true;
  // Single-pass, because a `transparent` material is otherwise drawn twice under
  // this backend when it is double-sided, and because there is nothing here that
  // wants a second pass even at `FrontSide` (2026-08-23 tracer lesson, part three).
  material.forceSinglePass = true;
  material.depthWrite = true;
  material.depthTest = true;
  // Distance along the beam, 0 at the arc and 1 at the tip — the geometry above
  // is built so this is exactly the cone's own `y`.
  const along = THREE.TSL.positionGeometry.y.clamp(0, 1);
  material.colorNode = THREE.TSL.mix(
    THREE.TSL.color(BEAM_NEAR_COLOUR),
    THREE.TSL.color(BEAM_FAR_COLOUR),
    along,
  );
  const falloff = THREE.TSL.smoothstep(1, FALLOFF_START, along);
  // Euclidean range from the lens, so turning your head cannot change which
  // beams are drawn — see `NEAR_FADE_START_M`.
  const lensRange = THREE.TSL.positionView.length();
  const nearFade = THREE.TSL.smoothstep(NEAR_FADE_START_M, NEAR_FADE_FULL_M, lensRange);
  const coverage = falloff
    .mul(nearFade)
    .mul(THREE.TSL.attribute<"float">("instanceIntensity", "float"))
    .mul(BEAM_MAX_COVERAGE);
  material.opacityNode = THREE.TSL.step(ditherThreshold(), coverage);
  material.alphaTest = BEAM_ALPHA_TEST;
  // A beam is light in air, not a lit surface, and this port has no `scene.fog`
  // at any hour anyway (`flight/sky-dressing.ts` records why). Same call the
  // tracers and the particles make.
  material.fog = false;
  return material;
}

function instancedAttribute(capacity: number, components: number): THREE.InstancedBufferAttribute {
  const attribute = new THREE.InstancedBufferAttribute(new Float32Array(capacity * components), components);
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}

/**
 * A fixed-capacity pool of beams.
 *
 * A class rather than a factory of closures for the reason `ParticleCloud` is
 * one: it holds mutable buffers and four methods.
 */
export class SearchlightBeams {
  readonly mesh: THREE.InstancedMesh;
  private readonly intensities: THREE.InstancedBufferAttribute;
  private written = 0;

  constructor(capacity: number) {
    const geometry = beamGeometry();
    this.intensities = instancedAttribute(capacity, 1);
    geometry.setAttribute("instanceIntensity", this.intensities);
    this.mesh = new THREE.InstancedMesh(geometry, searchlightBeamMaterial(), capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.renderOrder = SEARCHLIGHT_RENDER_ORDER;
    // The beams stand wherever the batteries are — kilometres apart, and none of
    // them at the origin — so a bounding sphere computed once over the unit cone
    // would cull the whole mesh permanently. Every pool in this renderer makes
    // the same call.
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
  }

  begin(): void {
    this.written = 0;
  }

  /**
   * Places one beam: apex at `(x, y, z)` — the lamp's aperture, which
   * `scene-searchlights.ts` lifts off the emplacement using the mount's own
   * trunnion height and lens offset — pointing along the unit vector
   * `(dirX, dirY, dirZ)`, at `intensity`.
   *
   * Returns `false` when the pool is full or the beam would draw nothing, which
   * a caller may treat the same way.
   */
  push(
    x: number,
    y: number,
    z: number,
    dirX: number,
    dirY: number,
    dirZ: number,
    intensity: number,
  ): boolean {
    if (this.written >= this.mesh.instanceMatrix.count) return false;
    if (intensity <= 0) return false;
    scratchDirection.set(dirX, dirY, dirZ);
    // A zero-length aim would give `setFromUnitVectors` nothing to rotate onto
    // and produce a NaN matrix, which under WebGPU takes the whole mesh with it.
    if (scratchDirection.lengthSq() < 1e-12) return false;
    scratchDirection.normalize();
    scratchPosition.set(x, y, z);
    scratchQuaternion.setFromUnitVectors(CONE_AXIS, scratchDirection);
    // Uniform, so the cone keeps its half-angle however long the beam is.
    scratchScale.setScalar(BEAM_LENGTH_M);
    scratchMatrix.compose(scratchPosition, scratchQuaternion, scratchScale);
    this.mesh.setMatrixAt(this.written, scratchMatrix);
    this.intensities.setX(this.written, intensity);
    this.written += 1;
    return true;
  }

  /** Publishes this frame's writes. An empty pool is hidden outright, so it costs no draw call at all. */
  end(): void {
    this.mesh.count = this.written;
    this.mesh.visible = this.written > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.intensities.needsUpdate = true;
  }

  /** How many beams the last `end()` published — what the draw-call count and the tests read. */
  get liveCount(): number {
    return this.written;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    // Freeing the material is what releases this render object's bindings under
    // the WebGPU backend (2026-08-19 world-stability lesson), not bookkeeping.
    (this.mesh.material as THREE.Material).dispose();
  }
}
